import { usePspOptions } from '@opengov/app-core/psp/context/psp.context';
import { useGetEntity } from '@opengov/bnp-core';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import React, { useEffect } from 'react';
import { Outlet, useNavigation, useParams } from 'react-router-dom';
import TopBarProgress from 'react-topbar-progress-indicator';
import { useInitializeFaro } from '../../application/instrument/grafanaFaro';
import { useInitializeOgAnalytics } from '../../application/instrument/ogIdentity';
import { useOgPageTracking } from '../../application/instrument/ogPageTracking';
import { useOgSignedInTracking } from '../../application/instrument/ogSessionEvents';
import { queryClient } from '../../src/apiHooks/core/queryClient';
import { useGetSessionV3 } from '../../src/apiHooks/session/useGetSessionV3';
import { LoadingPage } from '../../src/components/LoadingPage/LoadingPage';
import { reportsFoldersQueryKeys } from '../../src/pages/reportsPage/components/ReportsFolders/hooks/useGetReportsFolders/reportsFolders.queryKeys';
import { foldersAndReportsQueryKeys } from '../../src/pages/reportsPage/hooks/useGetFoldersAndReports/foldersAndReports.queryKeys';

TopBarProgress.config({
  barColors: {
    '0': capitalDesignTokens.foundations.colors.brand700,
    '1.0': capitalDesignTokens.foundations.colors.brand700
  },
  barThickness: 4,
  shadowBlur: 0
});

// In PSP the shell selects the active entity (`activeEntity.friendlyId`) and reporting then
// navigates the router to `/:entitySubdomain` to match it. Between the selection and the
// router catching up, the previous entity is still routed, so its (stale) reports would
// otherwise flash before the new entity's data loads. Returns true during that gap so the
// caller can show a loading state instead of the previous entity's listing.
export const isPspEntitySwitchPending = ({
  pspActiveEntitySubdomain,
  routedEntitySubdomain
}: {
  pspActiveEntitySubdomain?: string;
  routedEntitySubdomain?: string;
}): boolean =>
  !!pspActiveEntitySubdomain && !!routedEntitySubdomain && pspActiveEntitySubdomain !== routedEntitySubdomain;

// Clearing here is safe because the pending switch swaps the previous reports route for
// <LoadingPage />, so there are no active observers left to trigger a refetch of the entity
// we are leaving; the newly selected entity then fetches fresh once the router catches up.
const clearReportListingData = (): void => {
  queryClient.removeQueries(foldersAndReportsQueryKeys.all);
  queryClient.removeQueries(reportsFoldersQueryKeys.all);
};

export function Root() {
  const navigation = useNavigation();
  const { entitySubdomain } = useParams();
  const pspOptions = usePspOptions();
  const { data: session } = useGetSessionV3();
  const { data: entity } = useGetEntity(entitySubdomain);
  useInitializeFaro({ session, entity });
  useInitializeOgAnalytics({ session, entity });
  useOgPageTracking();
  useOgSignedInTracking({ session });

  const entitySwitchPending = isPspEntitySwitchPending({
    pspActiveEntitySubdomain: pspOptions?.activeEntity?.friendlyId,
    routedEntitySubdomain: entitySubdomain
  });

  useEffect(() => {
    if (entitySwitchPending) {
      clearReportListingData();
    }
  }, [entitySwitchPending]);

  return (
    <>
      {navigation.state === 'loading' ? <TopBarProgress /> : null}
      {entitySwitchPending ? <LoadingPage /> : <Outlet />}
    </>
  );
}
